const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

// Line ids must be unique within a page. The old `Date.now() + Math.random()` collided in
// roughly 1 import in 6 (a whole file is parsed inside one millisecond and the random part
// is squeezed into ~12 bits), and two lines sharing an id means deleting or editing one hits
// its twin. Strictly increasing, so it can't collide however many are made at once.
let lastLineId = 0;
export const newLineId = () => {
  lastLineId = Math.max(Date.now() * 1000, lastLineId + 1);
  return lastLineId;
};

// Tally exports are UTF-16 with a BOM, but depending on version and how the file was saved
// they can also be UTF-8 or BOM-less UTF-16. Reading with a fixed encoding turned any other
// encoding into garbage, so look at the first bytes instead.
export function decodeTallyBytes(buffer) {
  const bytes = new Uint8Array(buffer);
  let encoding = 'utf-8';
  if (bytes[0] === 0xFF && bytes[1] === 0xFE) encoding = 'utf-16le';
  else if (bytes[0] === 0xFE && bytes[1] === 0xFF) encoding = 'utf-16be';
  else if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) encoding = 'utf-8';
  else if (bytes.length > 3 && bytes[1] === 0 && bytes[3] === 0) encoding = 'utf-16le';
  else if (bytes.length > 3 && bytes[0] === 0 && bytes[2] === 0) encoding = 'utf-16be';
  return new TextDecoder(encoding).decode(bytes);
}

const isoDate = (y, m, d) => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null; // e.g. 31-Feb
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

// "14-Sep-26" (Tally's usual form) -> "2026-09-14". Also reads the other shapes a Tally date
// setting can produce. Returns null for anything that isn't a real calendar date, and the
// import check turns that into an error - a null date used to print as 01-01-1970.
export const parseTallyDate = (text) => {
  const s = String(text || '').trim();
  let m = s.match(/^(\d{1,2})[-\s/]([A-Za-z]{3,9})[-\s/,]*(\d{2}|\d{4})$/);
  if (m) {
    const month = MONTHS[m[2].slice(0, 3).toLowerCase()];
    if (!month) return null;
    return isoDate(m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10), month, parseInt(m[1], 10));
  }
  m = s.match(/^(\d{4})(\d{2})(\d{2})$/) || s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return isoDate(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) return isoDate(+m[3], +m[2], +m[1]);
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2})$/);
  if (m) return isoDate(2000 + +m[3], +m[2], +m[1]);
  return null;
};

// Tally escapes names in the export ("DISCOUNT &amp; KASAR"); show them as typed.
const decodeXmlEntities = (s) => s
  .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
  .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'")
  .replace(/&amp;/g, '&');

// null when the text isn't a number (an unreadable amount must not turn into NaN totals)
const parseAmount = (text) => {
  if (!text) return null;
  const n = parseFloat(String(text).replace(/,/g, ''));
  return Number.isFinite(n) ? Math.abs(n) : null;
};

const detectFuelType = (text) => {
  if (!text) return null;
  if (/diesel/i.test(text)) return 'Diesel';
  if (/petrol|\bms\b/i.test(text)) return 'Petrol';
  if (/xtra|\bxg\b/i.test(text)) return 'Xtragreen';
  return null;
};

const isCashText = (text) => /cash/i.test(text || '');
// The agreed way to mark cash given to a driver in the narration is "(CASH)"
const STANDARD_CASH_TAG = /\(\s*cash\s*\)/i;

// When a voucher lists several ledgers, prefer one we understand (fuel or cash)
const pickLedger = (ledgers) => ledgers.find(l => isCashText(l) || detectFuelType(l)) || ledgers[ledgers.length - 1] || null;

// The pump's selling rate for one product on one day (null if unknown) - used when a person
// picks the Item of a line by hand, so its quantity can be worked out from the amount
export async function lookupFuelRate(isoDate, product) {
  try {
    const res = await fetch(`/api/rates?date=${isoDate}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (!res.ok) return null;
    const rates = await res.json();
    return parseFloat(rates[String(product).toLowerCase()]) || null;
  } catch {
    return null;
  }
}

// A fuel line with no rate yet (its item or date was only just fixed by hand) gets that day's rate and
// the quantity worked out from the amount. setList is the page's setLines/setEntries.
export function fillMissingRate(line, date, item, setList) {
  if (!line || !['Petrol', 'Diesel', 'Xtragreen'].includes(item) || !date || parseFloat(line.rate) > 0) return;
  lookupFuelRate(date, item).then(rate => {
    if (!rate) return;
    // ignore the answer if the line changed again while the lookup was running
    setList(prev => prev.map(l => (l.id === line.id && l.item_name === item && l.business_date === date && !(parseFloat(l.rate) > 0)
      ? { ...l, rate, quantity: Math.round(((parseFloat(l.amount) || 0) / rate) * 100) / 100 }
      : l)));
  });
}

// Parses Tally's "flat" report XML export (one long stream of tags, not
// nested per-record) into { entries, receipts, skipped, importTotal, error } ready for the
// credit-sales review table.
// A record's fields all fall between the start and its own <DSPEXPLVCHNUMBER> tag.
export async function parseTallyXmlText(xmlText, partyName) {
  // Match only the specific leaf tags we need - matching any <TAG> generically would let
  // the lazy regex swallow the whole file as one "ENVELOPE" match (its closing tag is
  // only at the very end, so there's nothing to stop it early).
  const tagRegex = /<(DSPVCHDATE|DSPVCHTYPE|DSPVCHDRAMT|DSPVCHCRAMT|DSPVCHLEDACCOUNT|DSPVCHEXPLACCOUNT|VCHLEDNARREXPLOSION|DSPEXPLVCHNUMBER)>([\s\S]*?)<\/\1>/g;
  const blank = () => ({ date: null, ledgerHint: null, ledgers: [] });
  const records = [];
  let current = blank();
  let match;
  while ((match = tagRegex.exec(String(xmlText || ''))) !== null) {
    const [, name, rawValue] = match;
    const value = decodeXmlEntities(rawValue.trim());
    if (name === 'DSPVCHDATE') {
      if (value && !current.date) current.date = value;
    } else if (name === 'DSPVCHTYPE') {
      current.vchType = value;
    } else if (name === 'DSPVCHDRAMT') {
      const n = parseAmount(value);
      if (n !== null) current.amount = n;
    } else if (name === 'DSPVCHCRAMT') {
      const n = parseAmount(value);
      if (n !== null) current.creditAmount = n;
    } else if (name === 'DSPVCHLEDACCOUNT' || name === 'DSPVCHEXPLACCOUNT') {
      if (value && value !== '(as per details)' && !/round off/i.test(value)) {
        if (!current.ledgers.includes(value)) current.ledgers.push(value);
      }
    } else if (name === 'VCHLEDNARREXPLOSION') {
      current.narration = value;
    } else if (name === 'DSPEXPLVCHNUMBER') {
      current.voucherNo = value.replace(/^\(No\.\s*:\s*/, '').replace(/\)$/, '').trim();
      current.ledgerHint = pickLedger(current.ledgers);
      records.push(current);
      current = blank();
    }
  }

  const isSaleType = (t) => /^sales?$/i.test(t || '');
  const isReceiptType = (t) => /^(receipt|rcpt)$/i.test(t || '');
  const saleRecords = records.filter(r => isSaleType(r.vchType) && r.date && r.amount !== undefined);
  const receiptRecords = records.filter(r => isReceiptType(r.vchType) && r.date && r.creditAmount);

  // Everything in the file that is NOT turned into a sales line or a receipt (journals,
  // credit notes, a sale with no debit amount...). The importer used to drop these
  // silently - they're handed back so the page can show what was left out.
  const skipped = records
    .filter(r => !saleRecords.includes(r) && !receiptRecords.includes(r))
    // a record with no type, no amount and no ledger is just empty - anything else is something we didn't understand
    .filter(r => r.vchType || r.amount !== undefined || r.creditAmount !== undefined || r.ledgerHint)
    .map(r => ({
      type: r.vchType || '(unknown type)',
      business_date: parseTallyDate(r.date),
      voucher_no: r.voucherNo,
      amount: r.amount ?? r.creditAmount ?? null,
      ledger: r.ledgerHint || '',
      // Anything that reduces, reverses or settles a sale changes what is owed (a sale or receipt that
      // could not be read, a credit/debit note, a record with no type), so it's a warning
      level: !r.vchType || isSaleType(r.vchType) || isReceiptType(r.vchType)
        || /credit\s*note|sales?\s*return|debit\s*note|cr\.?\s*note|dr\.?\s*note/i.test(r.vchType) ? 'warn' : 'info'
    }));

  if (records.length === 0) {
    return { entries: [], receipts: [], skipped, importTotal: 0, error: 'This does not look like a Tally ledger export (no voucher records found). Export the creditor\'s ledger from Tally as XML.' };
  }
  if (saleRecords.length === 0) {
    return { entries: [], receipts: [], skipped, importTotal: 0, error: 'No Sale vouchers found in this XML.' };
  }
  const importTotal = Math.round(saleRecords.reduce((s, r) => s + r.amount, 0) * 100) / 100;

  // Payments the creditor made (a credit on their ledger). Not part of the sales
  // lines - the invoice shows them separately, as a deduction from what's owed.
  const receipts = receiptRecords
    .map(r => ({
      id: newLineId(),
      business_date: parseTallyDate(r.date),
      party_name: partyName,
      voucher_no: r.voucherNo,
      bank: r.ledgerHint ? r.ledgerHint.split(' - ')[0] : '',
      amount: r.creditAmount
    }));

  // Fetch each unique date's fuel rate once (needed to back-calculate quantity from the amount)
  const uniqueDates = [...new Set(saleRecords.map(r => parseTallyDate(r.date)).filter(Boolean))];
  const rateByDate = {};
  for (const date of uniqueDates) {
    try {
      const res = await fetch(`/api/rates?date=${date}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      rateByDate[date] = res.ok ? await res.json() : null;
    } catch {
      rateByDate[date] = null;
    }
  }

  const entries = saleRecords.map(r => {
    const date = parseTallyDate(r.date);
    const narration = r.narration || '';
    // Cash given to a driver is marked either in the narration ("...(CASH)") or by booking
    // the voucher to a cash sales ledger ("CASH SALES") instead of a fuel ledger.
    const cashInNarration = isCashText(narration);
    const isCash = cashInNarration || isCashText(r.ledgerHint);
    const vbMatch = narration.match(/VEHICLE\s*NO\/BILL\s*No\.?\s*([^\s/]+)\/(\d+)/i);
    const vehicleNo = vbMatch ? vbMatch[1] : '';
    const billNo = vbMatch ? vbMatch[2] : '';

    let itemName = null, quantity = 0, rate = null, amount = 0, cashAmount = 0;
    if (isCash) {
      cashAmount = r.amount;
      itemName = 'CASH';
    } else {
      amount = r.amount;
      const fuelType = detectFuelType(r.ledgerHint);
      itemName = fuelType;
      const rates = rateByDate[date];
      if (fuelType && rates) {
        rate = parseFloat(rates[fuelType.toLowerCase()]) || null;
        if (rate) quantity = Math.round((amount / rate) * 100) / 100;
      }
    }

    return {
      id: newLineId(),
      business_date: date,
      party_name: partyName,
      item_name: itemName,
      quantity,
      amount,
      vehicle_no: vehicleNo,
      bill_no: billNo,
      voucher_no: r.voucherNo,
      rate,
      cash_amount: cashAmount,
      // How the line was recognised as cash. 'narration' = the agreed "(CASH)" tag; anything else
      // ('narration-loose' = the word cash somewhere in the text, 'ledger' = only the ledger name)
      // is a guess, which the import check asks you to confirm
      cash_source: isCash ? (cashInNarration ? (STANDARD_CASH_TAG.test(narration) ? 'narration' : 'narration-loose') : 'ledger') : null,
      ledger_name: r.ledgerHint || '',
      // other ledgers on the same voucher (we only read one) - normally empty
      extra_ledgers: r.ledgers.filter(l => l !== r.ledgerHint)
    };
  });

  return { entries, receipts, skipped, importTotal, error: null };
}
